import React from "react";
import PropTypes from "prop-types";
import SubpageHeader from "../components/SubpageHeader";
import SiteFooter from "../components/SiteFooter";
import TrustPageIntro from "../components/TrustPageIntro";
import Typography from "../components/ui/Typography";
import usePageMeta from "../hooks/usePageMeta";
import { handleInternalLinkClick } from "../utils/navigation";
import { buildStaticPageSeoMeta } from "./static/staticPageSeo";

const faqGroups = [
  {
    title: "Choosing shows",
    description: "How shows earn a place in the guide.",
    entries: [
      {
        question: "Why isn't a show listed?",
        answer:
          "PremiereLens is selective by design. We focus on scripted, narrative television and filter out reality TV, game shows, talk shows, news, sports, soap operas, children's programming, and documentary series. Some niche or regional shows may also be missing from the source data.",
      },
      {
        question: "What does the 7.0 rating bar mean?",
        answer:
          "Returning shows need at least 7.0 on IMDb. New shows often arrive before they have a rating, so they're picked on their network, genre and description to make sure they're scripted shows. When IMDb hasn't rated a returning show, we use the viewer rating from the release listings.",
      },
      {
        question: "Why might a listed show have a rating below 7.0?",
        answer:
          "Only a new show can. New shows have no rating bar, so a first season can be listed before its rating settles. A returning show whose rating drops below 7.0 comes off the list.",
      },
      {
        question: "Which countries are covered?",
        answer:
          "We track releases from the US, UK, Germany, France, Sweden, Norway, Denmark, and Finland. US releases have the widest coverage window at roughly 31 days out; European releases are tracked closer to premiere, usually 7 days in advance.",
      },
    ],
  },
  {
    title: "Recommendations and search",
    description: "How PremiereLens explains why one show might lead to another.",
    entries: [
      {
        question: "How does PremiereLens choose recommendations?",
        answer:
          "Recommendations look for a similar mood, humour, pace or character relationships. Each pick has a short reason explaining its connection to the show you started from.",
      },
      {
        question: "What are shows-like pages?",
        answer:
          "A shows-like page starts with three recommendations for a show, followed by up to three deeper cuts. Each explains why it fits. The picks come from the premiere catalogue, so they are mostly recent releases and returning seasons.",
      },
      {
        question: "Why do only some shows have a shows-like page?",
        answer:
          "A shows-like page is published only when there are enough direct, explainable matches for that show. If the recommendation data is thin, stale, or not specific enough, the page stays unpublished instead of creating a weak similarity list.",
      },
      {
        question: "How does the Recommendations page rank shows?",
        answer:
          "The Recommendations page ranks shows by how often they appear in other shows’ recommendation lists. It measures how often a show is recommended, rather than ratings or viewing figures. Choose any show to see its own recommendations.",
        links: [
          {
            label: "Get TV recommendations",
            href: "/most-recommended",
          },
        ],
      },
      {
        question: "How does archive search work?",
        answer:
          "Archive search lets you describe the kind of show you want in natural language, such as slow-burn spy drama or cosy village mystery. It searches the archive by the meaning of the description, then falls back to regular keyword matching when meaning search is unavailable or too broad.",
        links: [
          {
            label: "Try archive search",
            href: "/archive",
          },
        ],
      },
    ],
  },
  {
    title: "Data and coverage",
    description: "Where the information comes from and how fresh it is.",
    entries: [
      {
        question: "Where do the show descriptions come from?",
        answer: "Descriptions draw on published listings from Watchmode, OMDb, TVMaze and TMDB. Where sources disagree about a show or season, the description can be held for review. The sources can still contain mistakes; matching their wording is not independent verification.",
      },
      {
        question: "How often is the data updated?",
        answer:
          "Show data refreshes every 24 hours. Release dates, ratings, and metadata come from Watchmode and TVMaze, with descriptions and additional details from OMDb, TheTVDB and TMDB.",
      },
      {
        question: "Can I browse past months?",
        answer:
          "Yes. The Archive page lets you search past shows by title or by taste, and it has a page for every month. Each month also has a best-of list of its highest-rated shows.",
        links: [
          {
            label: "Archive page",
            href: "/archive",
          },
        ],
      },
    ],
  },
  {
    title: "Using the site",
    description: "Saving, sharing, and keeping up with releases.",
    entries: [
      {
        question: "Where is My List stored?",
        answer:
          "My List is saved in your browser's local storage. It stays on your device and is never sent to our servers. Clearing browser data will erase it.",
      },
      {
        question: "Can I share a show?",
        answer:
          "Yes. Each show has a share button that uses your device's native share menu or copies the link to your clipboard.",
      },
      {
        question: "Is there an RSS feed?",
        answer:
          "Yes. Use the RSS feed in any feed reader to keep up with upcoming releases.",
        links: [
          {
            label: "/rss",
            href: "/rss",
            code: true,
            internal: false,
          },
          {
            label: "/feed.xml",
            href: "/feed.xml",
            code: true,
            internal: false,
          },
        ],
      },
    ],
  },
  {
    id: "privacy",
    title: "Privacy and ads",
    description: "How lightweight the experience stays.",
    entries: [
      {
        question: "Do you show ads?",
        answer:
          "Not currently. If that changes, the privacy policy will be updated before the site asks more of you.",
        links: [
          {
            label: "privacy policy",
            href: "/privacy",
          },
        ],
      },
      {
        question: "Does archive search use My List or my browsing history?",
        answer:
          "My List can move similar shows higher in the results, entirely in your browser. Your list, browsing history and analytics consent state are not sent with the search request.",
        links: [
          {
            label: "privacy policy",
            href: "/privacy",
          },
        ],
      },
    ],
  },
];

const faqEntries = faqGroups.flatMap((group) => group.entries);

// Stable anchor ids: the slug of the text, so /faq#privacy and each question can be linked.
export const slugifyAnchor = (text) =>
  text
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const faqAnchors = faqGroups.map((group) => ({
  id: group.id || slugifyAnchor(group.title),
  title: group.title,
  questions: group.entries.map((entry) => ({ id: slugifyAnchor(entry.question), question: entry.question })),
}));

function linkedAnswerText(entry) {
  if (!Array.isArray(entry.links) || entry.links.length === 0) return entry.answer;
  const suffix = entry.links.map((link) => link.label).join(", ");
  return `${entry.answer} Related link${entry.links.length === 1 ? "" : "s"}: ${suffix}.`;
}

function InlineLink({ href, children, code = false, internal = true }) {
  return (
    <a
      href={href}
      onClick={internal ? (event) => handleInternalLinkClick(event, href) : undefined}
      className="font-semibold text-primary underline-offset-4 transition-colors hover:text-secondary-readable hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
    >
      {code ? <code className="rounded bg-muted px-1.5 py-0.5 text-body-small text-foreground underline decoration-primary underline-offset-4">{children}</code> : children}
    </a>
  );
}

InlineLink.propTypes = {
  href: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  code: PropTypes.bool,
  internal: PropTypes.bool,
};

const FaqPage = () => {
  usePageMeta(buildStaticPageSeoMeta("faq"));

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqEntries.map((entry) => ({
      "@type": "Question",
      name: entry.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: linkedAnswerText(entry),
      },
    })),
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <SubpageHeader />

      <main id="main-content" tabIndex={-1} className="route-shell route-shell--editorial">
        <div className="mx-auto max-w-5xl space-y-10">
          <TrustPageIntro
            title="Frequently asked questions"
            description="Short answers about how PremiereLens keeps the guide selective, how the data stays current, and what happens to your saved shows."
          />

          <nav aria-label="Question groups">
            <ul className="flex flex-wrap gap-x-6 gap-y-2">
              {faqAnchors.map((group) => (
                <li key={group.id}>
                  <a
                    href={`#${group.id}`}
                    className="font-semibold text-primary underline underline-offset-4 transition-colors hover:text-secondary-readable focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {group.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {faqGroups.map((group, groupIndex) => (
            <section
              key={group.title}
              id={faqAnchors[groupIndex].id}
              className="scroll-mt-24 grid gap-5 border-b border-dashed border-accent-gold/50 pb-8 last:border-b-0 md:grid-cols-[minmax(0,0.8fr)_minmax(0,1.6fr)] md:gap-8"
            >
              <div>
                <Typography as="h2" variant="h2">
                  {group.title}
                </Typography>
                <Typography variant="body" className="mt-2 text-muted-foreground">
                  {group.description}
                </Typography>
              </div>

              <div className="grid gap-6">
                {group.entries.map((entry, entryIndex) => (
                  <article key={entry.question} id={faqAnchors[groupIndex].questions[entryIndex].id} className="scroll-mt-24">
                    <Typography as="h3" variant="h3">
                      {entry.question}
                    </Typography>
                    <Typography variant="body" className="reading-measure mt-2 text-foreground/85">
                      {entry.answer}
                      {entry.links?.length > 0 && (
                        <>
                          {" "}
                          {entry.links.map((link, index) => (
                            <React.Fragment key={link.href}>
                              {index === 0 ? "" : " "}
                              <InlineLink href={link.href} code={link.code} internal={link.internal !== false}>
                                {link.label}
                              </InlineLink>
                              {index < entry.links.length - 1 ? "," : "."}
                            </React.Fragment>
                          ))}
                        </>
                      )}
                    </Typography>
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

FaqPage.propTypes = {};

export default FaqPage;
